// /quotes/new: start a quote from a postcode. We list the homes with an EPC,
// pre-fill the property answers from the chosen home's certificate (the same
// GOV.UK look-up the Full widget uses), take the customer's details, and create
// the quote: the API works out the heat loss (RdSAP 10) and prices every
// package straight away. From Leads, ?uprn=&line= skips the search.
// Homes with no EPC: enter the address by hand and answer the questions.
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, MapPin, Search } from "lucide-react";
import type { EpcCertificate, FullProperty } from "@widget";
import { useQuotesApi } from "../../quotes/api";
import { completeProperty, day } from "../../quotes/format";
import { PropertyForm } from "../../quotes/PropertyForm";
import "../../quotes/quotes.css";

const POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;
const postcodeOf = (line: string) => (line.match(/([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\s*$/i)?.[1] ?? "").toUpperCase();

export function NewQuotePage() {
  const api = useQuotesApi();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [postcode, setPostcode] = useState(params.get("postcode") ?? "");
  const [addresses, setAddresses] = useState<{ id: string; line: string }[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [address, setAddress] = useState<{ id: string; line: string } | null>(params.get("uprn") ? { id: params.get("uprn")!, line: params.get("line") ?? "" } : null);
  const [manual, setManual] = useState(false);
  const [manualLine, setManualLine] = useState("");
  const [certificate, setCertificate] = useState<EpcCertificate | null>(null);
  const [epcProperty, setEpcProperty] = useState<Partial<FullProperty> | null>(null);
  const [property, setProperty] = useState<FullProperty | null>(null);
  const [loadingHome, setLoadingHome] = useState(false);
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function search(e?: FormEvent) {
    e?.preventDefault();
    if (!POSTCODE_RE.test(postcode.trim())) return setError("Enter a valid UK postcode.");
    setSearching(true);
    setError(null);
    setAddress(null);
    setProperty(null);
    try {
      const r = await api.surveyAddresses(postcode.trim());
      setAddresses(r.addresses);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSearching(false);
    }
  }

  // A home was chosen: fetch its EPC and pre-fill the answers
  useEffect(() => {
    if (!address) return;
    let live = true;
    setLoadingHome(true);
    setError(null);
    api
      .surveyProperty(address.id, address.line)
      .then((r) => {
        if (!live) return;
        setCertificate(r.full?.certificate ?? null);
        setEpcProperty(r.full?.property ?? null);
        setProperty(completeProperty(r.full?.property ?? {}));
      })
      .catch((err: Error) => live && setError(err.message))
      .finally(() => live && setLoadingHome(false));
    return () => {
      live = false;
    };
  }, [api, address]);

  function startManual() {
    setManual(true);
    setAddress(null);
    setCertificate(null);
    setEpcProperty(null);
    setProperty(completeProperty({}));
  }

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!property) return;
    const line = manual ? manualLine.trim() : address?.line ?? "";
    if (line.length < 3) return setError("Enter the address.");
    setCreating(true);
    setError(null);
    try {
      const q = await api.createQuote({
        customer,
        address: { line, uprn: manual ? "" : address?.id ?? "", postcode: (postcodeOf(line) || postcode).trim().toUpperCase() },
        survey: { property, grantAnswers: { owner: true, newBuild: false, previousFunding: false, mainsGas: null }, includeGrant: true },
        source: params.get("widgetId") ? { widgetId: params.get("widgetId")!, uprn: address?.id } : undefined,
      });
      navigate(`/quotes/${q.quoteId}`, { replace: true });
    } catch (err) {
      setError((err as Error).message);
      setCreating(false);
    }
  }

  return (
    <div className="page q-page q-new">
      <div className="page-head">
        <h1>New quote</h1>
        <p>Find the home, check its details and add your customer. We'll work out the heat loss and price each of your packages for it.</p>
      </div>

      {error && <div className="flash flash-danger">{error}</div>}

      {/* 1. The home */}
      <section className="card">
        <h2>
          <span className="q-step">1</span> The home
        </h2>
        {!manual && !params.get("uprn") && (
          <form className="q-search-row" onSubmit={(e) => void search(e)}>
            <label className="icon-input">
              <MapPin size={16} aria-hidden="true" />
              <input className="input" placeholder="Postcode, e.g. LS6 3HN" value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())} aria-label="Postcode" />
            </label>
            <button type="submit" className="btn btn-primary" disabled={searching}>
              <Search size={16} aria-hidden="true" /> {searching ? "Searching…" : "Find address"}
            </button>
          </form>
        )}

        {addresses && !manual && !address && (
          <div className="q-address-list" role="listbox" aria-label="Addresses">
            {addresses.length === 0 && <p className="muted">No homes with an EPC at this postcode.</p>}
            {addresses.map((a) => (
              <button key={a.id} type="button" role="option" aria-selected={false} className="q-address" onClick={() => setAddress(a)}>
                {a.line}
              </button>
            ))}
          </div>
        )}

        {address && (
          <div className="q-chosen">
            <MapPin size={16} aria-hidden="true" />
            <strong>{address.line}</strong>
            {!params.get("uprn") && (
              <button type="button" className="link-button small" onClick={() => setAddress(null)}>
                Change
              </button>
            )}
          </div>
        )}

        {!manual ? (
          <button type="button" className="link-button small q-manual-link" onClick={startManual}>
            Home not listed (no EPC)? Enter it by hand
          </button>
        ) : (
          <div className="ed-field">
            <label className="ed-label" htmlFor="q-manual-line">
              Address (with postcode)
            </label>
            <input id="q-manual-line" className="input" value={manualLine} onChange={(e) => setManualLine(e.target.value)} placeholder="1 Example Street, Leeds, LS1 1AA" />
          </div>
        )}

        {loadingHome && <p className="muted">Looking up the EPC…</p>}
        {certificate && (
          <p className="q-epc-line">
            EPC {certificate.current.band ?? "?"} ({certificate.current.score ?? "?"}), certificate {certificate.number}, lodged {day(certificate.lodgedDate)}. Answers marked{" "}
            <span className="q-epc-tag">EPC</span> come from it.
          </p>
        )}
        {address && !loadingHome && !certificate && property && <p className="muted">No full certificate found for this home: check the answers below.</p>}
      </section>

      {/* 2. Its details */}
      {property && (
        <form onSubmit={(e) => void create(e)}>
          <section className="card">
            <h2>
              <span className="q-step">2</span> Check the details
            </h2>
            <p className="card-sub">These drive the heat loss. You can change them, or enter your own survey figure, in the quote.</p>
            <PropertyForm value={property} epc={epcProperty} onChange={setProperty} />
          </section>

          {/* 3. The customer */}
          <section className="card">
            <h2>
              <span className="q-step">3</span> Your customer
            </h2>
            <div className="q-customer-grid">
              <div className="ed-field">
                <label className="ed-label" htmlFor="q-name">
                  Name
                </label>
                <input id="q-name" className="input" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} autoComplete="off" />
              </div>
              <div className="ed-field">
                <label className="ed-label" htmlFor="q-email">
                  Email
                </label>
                <input id="q-email" className="input" type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} autoComplete="off" />
              </div>
              <div className="ed-field">
                <label className="ed-label" htmlFor="q-phone">
                  Phone
                </label>
                <input id="q-phone" className="input" type="tel" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} autoComplete="off" />
              </div>
            </div>
            <div className="q-actions">
              <button type="submit" className="btn btn-primary" disabled={creating}>
                {creating ? "Working out the heat loss and prices…" : "Create quote"} <ArrowRight size={16} aria-hidden="true" />
              </button>
            </div>
          </section>
        </form>
      )}
    </div>
  );
}
